/**
 * Unified HTTP client for SatAgrySys frontend.
 * Set REACT_APP_USE_MOCK_API=true to use in-memory test APIs (no backend required).
 */

export const isMockApiEnabled = () => {
  // Allow runtime override via localStorage for easier dev testing.
  const runtime = localStorage.getItem("useMockApi");
  if (runtime !== null) return String(runtime).toLowerCase() === "true";
  return String(process.env.REACT_APP_USE_MOCK_API || "true").toLowerCase() === "true";
};

export const getApiBaseUrl = () => {
  const base = process.env.REACT_APP_API_BASE_URL || "http://178.131.60.38:8000/api";
  return base.replace(/\/$/, "");
};

export const getAuthToken = () => localStorage.getItem("authToken");

export const getAuthHeaders = (extra = {}) => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

/**
 * Build absolute URL for same-origin /api paths (dev proxy) or full backend URL.
 */
export const buildUrl = (path) => {
  if (path.startsWith("http")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized.startsWith("/api")) {
    return normalized;
  }
  return `${getApiBaseUrl()}${normalized}`;
};

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch(path, options = {}) {
  const { mockHandler, ...fetchOptions } = options;

  if (isMockApiEnabled() && typeof mockHandler === "function") {
    const mockResult = await mockHandler();
    if (mockResult && mockResult.__error) {
      throw new ApiError(mockResult.__error.message, {
        status: mockResult.__error.status,
        body: mockResult.__error.body,
      });
    }
    return mockResult;
  }

  const url = buildUrl(path);
  const response = await fetch(url, {
    ...fetchOptions,
    headers: getAuthHeaders(fetchOptions.headers),
  });

  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await response.json().catch(() => ({})) : await response.text();

  if (!response.ok) {
    const detail =
      (typeof body === "object" && (body.detail || body.message)) ||
      `Request failed (${response.status})`;
    throw new ApiError(detail, { status: response.status, body });
  }

  return body;
}
